import { create } from "zustand";
import { TutorExtendedProfileData, TutorSectionType } from "src/interfaces/TutorProfile";
import { useProfileStore } from "src/store/profileStore";
import { supabase } from 'src/lib/supabaseClient';
import { camelToSnake } from 'src/lib/utils';
import { normalizeTutorEducation } from "src/utils/tutorEducation";


// Create a store for the extended tutor profile
interface TutorExtendedProfileStore {
  profileData: TutorExtendedProfileData;
  editData: Partial<TutorExtendedProfileData>;
  pendingChanges: Partial<TutorExtendedProfileData>;
  hasPendingChanges: boolean;
  isLoading: boolean;
  error: string | null;
  isEditing: boolean;
  photoUploadOpen: boolean;
  uploadType: 'profile' | 'gallery' | 'cv';
  selectedFile: File | null;
  uploadStatus: 'idle' | 'uploading' | 'success' | 'error';
  activeSectionEdit: TutorSectionType | null;
  modifiedSections: Set<TutorSectionType>;

  // Actions
  fetchProfile: (userId: string, authProfileData?: any) => Promise<void>;
  updateProfile: (data: Partial<TutorExtendedProfileData>) => Promise<void>;
  setIsEditing: (isEditing: boolean) => void;
  setEditData: (data: Partial<TutorExtendedProfileData>) => void;
  updateEditData: (field: string, value: any) => void;
  updateNestedEditData: (parent: string, field: string, value: any) => void;
  resetEditData: () => void;
  setPhotoUploadOpen: (isOpen: boolean) => void;
  setUploadType: (type: 'profile' | 'gallery' | 'cv') => void;
  setSelectedFile: (file: File | null) => void;
  setUploadStatus: (status: 'idle' | 'uploading' | 'success' | 'error') => void;
  handleOpenPhotoUpload: (type: 'profile' | 'gallery' | 'cv') => void;
  setActiveSectionEdit: (section: TutorSectionType | null) => void;

  // New actions for pending changes
  addPendingChanges: (section: TutorSectionType, changes: Partial<TutorExtendedProfileData>) => void;
  saveAllChanges: () => Promise<void>;
  discardAllChanges: () => void;
  getDisplayData: () => TutorExtendedProfileData;
  isSectionModified: (section: TutorSectionType) => boolean;
}

// Create the store
export const useTutorExtendedProfileStore = create<TutorExtendedProfileStore>((set, get) => ({
  profileData: {
    id: "",
    firstName: "",
    lastName: "",
    email: "",
    userType: "tutor",
    createdAt: null,
    updatedAt: null,
    profilePictureUrl: null,

    // Tutor-specific fields
    education: [],
    hourly_rate: null,
    subjects_taught: [],
    experience: [],
    bio: "",
    verification_status: "pending",
    cv_file_path: "",
    rating: null,
    date_of_birth: null,

    // New enhanced fields
    achievements: [],
    expertise: [],

    // Availability data
    availabilitySlots: [],

    // Additional fields
    location: ""
  },
  editData: {},
  pendingChanges: {},
  hasPendingChanges: false,
  modifiedSections: new Set<TutorSectionType>(),
  isLoading: true,
  error: null,
  isEditing: false,
  photoUploadOpen: false,
  uploadType: 'profile',
  selectedFile: null,
  uploadStatus: 'idle',
  activeSectionEdit: null,

  fetchProfile: async (userId, authProfileData = null) => {
    set({ isLoading: true, error: null });

    try {
      // Define empty tutor data defaults
      const emptyTutorData = {
        education: [],
        hourly_rate: null,
        subjects_taught: [],
        experience: [],
        bio: "",
        verification_status: "pending",
        cv_file_path: "",
        rating: null,
        date_of_birth: null,
        achievements: [],
        expertise: [],
        availabilitySlots: [],
        location: ""
      };

      // If we don't have authProfileData, we can't proceed
      if (!authProfileData) {
        console.log("No profile data available, using empty defaults");
        set({
          profileData: {
            id: userId,
            firstName: "",
            lastName: "",
            email: "",
            userType: "tutor",
            profilePictureUrl: null,
            createdAt: null,
            updatedAt: null,
            ...emptyTutorData
          },
          isLoading: false,
          isEditing: false
        });
        return;
      }

      // Extract all profile data from passed authProfileData
      const profileData = {
        id: userId,
        firstName: authProfileData.firstName || "",
        lastName: authProfileData.lastName || "",
        email: authProfileData.email || "",
        userType: "tutor",
        profilePictureUrl: authProfileData.profilePictureUrl || null,
        createdAt: authProfileData.createdAt || null,
        updatedAt: authProfileData.updatedAt || null,
        
        // Tutor-specific fields - use camelCase from AuthContext
        education: normalizeTutorEducation(authProfileData.education),
        hourly_rate: authProfileData.hourlyRate || null,
        subjects_taught: authProfileData.subjectsTaught || [],
        experience: authProfileData.experience || [],
        bio: authProfileData.bio || "",
        verification_status: authProfileData.verificationStatus || "pending", 
        cv_file_path: authProfileData.cvFilePath || "",
        rating: authProfileData.rating || null,
        date_of_birth: authProfileData.dateOfBirth || null,
        achievements: authProfileData.achievements || [],
        expertise: authProfileData.expertise || [],
        availabilitySlots: authProfileData.availabilitySlots || [],
        location: authProfileData.location || ""
      };

      // Set the profile data in the store
      set({
        profileData,
        isLoading: false,
        isEditing: false
      });
    } catch (error) {
      console.error("Error processing tutor profile data:", error);
      set({
        error: error instanceof Error ? error.message : "Failed to load profile",
        isLoading: false
      });
    }
  },

  updateProfile: async (data) => {
    // Don't set global loading state for profile updates to prevent UI flickering
    set({ error: null });

    try {

      // Update the profiles table if profile picture URL is provided
      if (data.profilePictureUrl !== undefined) {
        const { error: profileError } = await supabase
          .from("profiles")
          .update(camelToSnake({
            profilePictureUrl: data.profilePictureUrl,
            updatedAt: new Date().toISOString()
          }))
          .eq("id", data.id);

        if (profileError) {
          throw profileError;
        }
      }

      // Extract tutor-specific data to save to the tutors table
      const tutorData: any = {
        education: data.education,
        hourly_rate: data.hourly_rate,
        subjects_taught: data.subjects_taught,
        experience: data.experience,
        bio: data.bio,
        cv_file_path: data.cv_file_path,
        rating: data.rating,
        date_of_birth: data.date_of_birth,
        achievements: data.achievements,
        expertise: data.expertise
      };

      // Only include verification_status if it's a valid value
      if (data.verification_status && 
          ['pending', 'verified', 'rejected', 'suspended'].includes(data.verification_status)) {
        tutorData.verification_status = data.verification_status;
      }

      // Save the tutor data to Supabase (only if there's tutor-specific data)
      const hasTutorData = Object.values(tutorData).some(value =>
        value !== undefined && value !== null &&
        (Array.isArray(value) ? value.length > 0 : value !== "")
      );

      if (hasTutorData) {
        const { error: updateError } = await supabase
          .from("tutors")
          .upsert(camelToSnake({
            id: data.id,
            ...tutorData
          }));

        if (updateError) {
          throw updateError;
        }
      }

      // Also update the global profile store to keep data in sync
      const profileStoreUpdate: any = {
        education: data.education,
        hourly_rate: data.hourly_rate,
        subjects_taught: data.subjects_taught,
        experience: data.experience,
        bio: data.bio,
        cv_file_path: data.cv_file_path,
        rating: data.rating,
        date_of_birth: data.date_of_birth,
        achievements: data.achievements,
        expertise: data.expertise,
        location: data.location
      };

      // Only include verification_status in profile store if it's valid
      if (data.verification_status && 
          ['pending', 'verified', 'rejected', 'suspended'].includes(data.verification_status)) {
        profileStoreUpdate.verification_status = data.verification_status;
      }

      // Include profile picture URL if it was updated
      if (data.profilePictureUrl !== undefined) {
        profileStoreUpdate.profilePictureUrl = data.profilePictureUrl;
      }

      useProfileStore.getState().setProfileData(profileStoreUpdate);

      // Update the profile data in the local store without setting loading state
      set(state => ({
        profileData: { ...state.profileData, ...data },
        isEditing: false
      }));
    } catch (error) {
      console.error("Error updating tutor profile:", error);
      set({
        error: error instanceof Error ? error.message : "Failed to update profile"
      });
    }
  },

  setIsEditing: (isEditing) => set({ isEditing }),

  setEditData: (data) => set({ editData: data }),

  updateEditData: (field, value) => set(state => ({
    editData: { ...state.editData, [field]: value }
  })),

  updateNestedEditData: (parent, field, value) => set(state => {
    // Create a deep copy of the nested object
    const parentObj = state.editData[parent as keyof typeof state.editData];
    const updatedParentObj = parentObj ? { ...parentObj as object } : {};

    // Set the new value
    return {
      editData: {
        ...state.editData,
        [parent]: {
          ...updatedParentObj,
          [field]: value
        }
      }
    };
  }),

  resetEditData: () => set({ editData: {} }),

  setPhotoUploadOpen: (isOpen) => set({ photoUploadOpen: isOpen }),

  setUploadType: (type) => set({ uploadType: type }),

  setSelectedFile: (file) => set({ selectedFile: file }),

  setUploadStatus: (status) => set({ uploadStatus: status }),

  handleOpenPhotoUpload: (type) => {
    set({
      uploadType: type,
      photoUploadOpen: true,
      selectedFile: null,
      uploadStatus: 'idle'
    });
  },

  setActiveSectionEdit: (section) => set({ activeSectionEdit: section }),

  // New methods for pending changes
  addPendingChanges: (section, changes) => set(state => {
    // Use a type assertion to handle the complex nested structure
    const newPendingChanges = { ...state.pendingChanges } as any;

    // Deep merge the changes into pending changes
    const deepMerge = (target: any, source: any) => {
      Object.keys(source).forEach(key => {
        if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
          // If the key doesn't exist in target or is not an object, create it
          if (!target[key] || typeof target[key] !== 'object') {
            target[key] = {};
          }
          deepMerge(target[key], source[key]);
        } else {
          // For primitives and arrays, just replace the value
          target[key] = source[key];
        }
      });
      return target;
    };

    // Apply deep merge
    deepMerge(newPendingChanges, changes);

    // Add the section to the modified sections set
    const newModifiedSections = new Set(state.modifiedSections);
    newModifiedSections.add(section);

    return {
      pendingChanges: newPendingChanges as Partial<TutorExtendedProfileData>,
      hasPendingChanges: true,
      modifiedSections: newModifiedSections
    };
  }),

  saveAllChanges: async () => {
    const state = get();

    if (!state.hasPendingChanges) return;

    try {
      // Combine profile data with pending changes
      const updatedData = {
        ...state.profileData,
        ...state.pendingChanges
      };

      // Call the existing updateProfile method to save to the database
      await state.updateProfile(updatedData);

      // After successful save, update the profileData with the saved changes
      // and clear pending changes
      set({
        profileData: updatedData,
        pendingChanges: {},
        hasPendingChanges: false,
        modifiedSections: new Set<TutorSectionType>()
      });
    } catch (error) {
      // If save fails, don't clear pending changes
      console.error('Error saving changes:', error);
      throw error;
    }
  },

  discardAllChanges: () => set({
    pendingChanges: {},
    hasPendingChanges: false,
    modifiedSections: new Set<TutorSectionType>()
  }),

  getDisplayData: () => {
    const state = get();
    // Combine profile data with pending changes to get the current display data
    return {
      ...state.profileData,
      ...state.pendingChanges
    } as TutorExtendedProfileData;
  },

  isSectionModified: (section) => {
    const state = get();
    return state.modifiedSections.has(section);
  }
}));
